<script setup lang="ts">
import LabelError from '../LabelError.vue';
import LabelBase from '../LabelBase.vue';
import InputBase from './InputBase.vue';

import { EFormItemSize } from '../../models/size';
import { ref } from 'vue';
import type { TInputFilledProps } from '../../models/TInputFilledProps';

const modelValue = defineModel<string>({ required: true });

const props = withDefaults(defineProps<TInputFilledProps>(), {
  size: EFormItemSize.m40,
  isRequired: false,
  error: '',
  placeholder: '',
  label: '',
});

const emit = defineEmits<{
  (e: 'focus' | 'blur'): void;
}>();

const isFocused = ref(false);

const inputBaseEl = ref();

function onWrapperClicked() {
  inputBaseEl.value?.focus();
}

const focus = () => inputBaseEl.value?.focus();

defineExpose({ focus });

function onFocus() {
  isFocused.value = true;
  emit('focus');
}

function onBlur() {
  isFocused.value = false;
  emit('blur');
}
</script>

<template>
  <div class="input-filled-wrapper" @click="onWrapperClicked">
    <LabelBase :label="label" :is-required="isRequired" :is-disabled="disabled" />

    <div
      class="input-filled-field"
      :class="{
        'input-filled-field_focused': isFocused,
        'input-filled-field_error': error,
        [`input-filled-field_${size}`]: size,
      }"
    >
      <span v-if="$slots['prepend-icon']" class="input-filled-field__prepend-icon">
        <slot name="prepend-icon" />
      </span>

      <InputBase
        ref="inputBaseEl"
        v-model="modelValue"
        :is-invalid="!!error"
        :size="size"
        :placeholder="placeholder"
        class="input-filled-field__input"
        @focus="onFocus"
        @blur="onBlur"
      >
        <template #prefix>
          <slot name="prefix" />
        </template>
      </InputBase>
    </div>

    <LabelError class="input-filled-wrapper__error-label" :error-text="error" />
  </div>
</template>

<style lang="stylus" scoped>
.input-filled-wrapper
  display flex
  flex-direction column
  gap 8px
  position relative

  &__error-label
    position absolute
    bottom -26px
    left 0
    right 0

.input-filled-field
  display flex
  align-items center
  padding 0 12px 0 6px
  cursor text
  background-color #F2F4F7
  border solid 1px #F2F4F7
  border-radius var(--rounding150)
  &:hover
    border solid 1px var(--border-focused)
  &_focused
    border solid 1px var(--border-focused)
  &_error
    border solid 1px var(--border-negative)!important

  &__prepend-icon
    font-size 24px
    padding 6px
    display flex
    align-items center

  &_m40
    height 40px
  &_l48
    height 48px

  &__input
    margin-left 10px
</style>
